import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EntityDocuments } from "@/pages/documents/EntityDocuments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Presentation, Library, Boxes, CalendarClock, ClipboardList, IdCard, Clock, FileText, StickyNote, AlertTriangle, Plus, Trash2, Archive, Pencil } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

export default function TeacherProfile() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const { id } = useParams();
  const canManage = hasRole("super_admin", "direction", "administration", "coordinateur");
  const canManageStatus = hasRole("super_admin", "direction", "administration");
  const [p, setP] = useState(null);
  const [tt, setTt] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const saveEdit = async (v) => { try { await api.put(`/people/teachers/${id}`, { name: v.name, email: v.email, phone: v.phone }); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const load = useCallback(() => {
    api.get(`/people/teachers/${id}/profile`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    api.get(`/people/teachers/${id}/timetable`).then((r) => setTt(r.data)).catch(() => {});
    api.get(`/people/teachers/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) api.get(`/people/teachers/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {}); }, [id, canManage]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/people/teachers/${id}/notes`, { text: noteText }); setNoteText(""); api.get(`/people/teachers/${id}/notes`).then((r) => setNotes(r.data)); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delNote = async (nid) => { try { await api.delete(`/people/teachers/${id}/notes/${nid}`); api.get(`/people/teachers/${id}/notes`).then((r) => setNotes(r.data)); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const archive = async () => {
    const msg = p.has_assignments ? "Ce professeur a des affectations actives. Archiver quand même ?" : `${t("archive")} ?`;
    if (!window.confirm(msg)) return;
    try { const { data } = await api.patch(`/people/teachers/${id}/archive`); data.warning ? toast.warning(data.warning) : toast.success(t("teacherArchived")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const reactivate = async () => { try { await api.patch(`/people/teachers/${id}/reactivate`); toast.success(t("teacherReactivated")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  if (!p) return <div className="text-slate-400">{t("loading")}</div>;
  return (
    <div data-testid="teacher-profile">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Presentation className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-slate-900">{p.name} <span className="font-mono text-sm text-slate-400">{p.teacher_code}</span></h1>
            <p className="text-sm text-slate-500">{p.email || "—"} · {p.phone || "—"} · {p.active_year || "—"}</p>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1" data-testid="teacher-edit-btn" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" />{t("editTeacher")}</Button>
            {canManageStatus && p.status !== "archived" && <Button size="sm" variant="outline" className="gap-1 text-red-600" data-testid="teacher-archive-btn" onClick={archive}><Archive className="h-4 w-4" />{t("archive")}</Button>}
            {canManageStatus && p.status === "archived" && <Button size="sm" variant="outline" className="gap-1 text-emerald-600" data-testid="teacher-reactivate-btn" onClick={reactivate}><Archive className="h-4 w-4" />{t("reactivate")}</Button>}
          </div>
        )}
      </div>
      <EntityDialog open={editOpen} onOpenChange={setEditOpen} title={t("editTeacher")} onSubmit={saveEdit}
        initial={{ name: p.name, email: p.email, phone: p.phone }}
        fields={[{ name: "name", label: t("name") }, { name: "email", label: t("email") }, { name: "phone", label: t("phone") }]} />
      {p.status !== "archived" && !p.has_assignments && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700" data-testid="teacher-no-assign-alert">
          <AlertTriangle className="h-4 w-4" />{t("noAssignmentsAlert")}
        </div>
      )}
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-toverview"><Presentation className="h-4 w-4 mr-2" />{t("overview")}</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-tassign"><ClipboardList className="h-4 w-4 mr-2" />{t("assignments")}</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-tsubjects"><Library className="h-4 w-4 mr-2" />{t("subject")}</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-tgroups"><Boxes className="h-4 w-4 mr-2" />{t("group")}</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-tschedule"><CalendarClock className="h-4 w-4 mr-2" />{t("schedule")}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-tdocuments"><FileText className="h-4 w-4 mr-2" />{t("documentsTab")}</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-ttimeline"><Clock className="h-4 w-4 mr-2" />{t("timeline")}</TabsTrigger>
          {canManage && <TabsTrigger value="notes" data-testid="tab-tnotes"><StickyNote className="h-4 w-4 mr-2" />{t("internalNotes")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-slate-200 p-5 max-w-lg space-y-2 text-sm" data-testid="teacher-overview">
            <div className="flex justify-between"><span className="text-slate-500">{t("teacherCode")}</span><span className="font-mono text-slate-800">{p.teacher_code}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("email")}</span><span className="text-slate-800">{p.email || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("phone")}</span><span className="text-slate-800">{p.phone || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("status")}</span><Badge className={`border-0 ${p.status === "archived" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{t(`ust_${p.status || "active"}`) !== `ust_${p.status || "active"}` ? t(`ust_${p.status || "active"}`) : (p.status || "active")}</Badge></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("activeYear")}</span><span className="text-slate-800">{p.active_year || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("levelsCovered")}</span><span className="text-slate-800">{(p.levels || []).map((l) => l.name).join(", ") || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("account")}</span><span className="text-slate-800">{p.user_account?.email || "—"} ({p.user_account?.status || "—"})</span></div>
          </Card>
        </TabsContent>

        <TabsContent value="assignments">
          {canManage && <div className="mb-2"><Link to="/school/timetable"><Button size="sm" variant="outline" className="gap-1" data-testid="manage-assignments-btn"><ClipboardList className="h-4 w-4" />{t("manageAssignments")}</Button></Link></div>}
          <Card className="card-float border-slate-200 overflow-hidden" data-testid="teacher-assignments">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50/70 text-left text-slate-500"><th className="p-3">{t("subject")}</th><th className="p-3">{t("group")}</th><th className="p-3">{t("status")}</th><th className="p-3">{t("date")}</th></tr></thead>
              <tbody>
                {p.assignments_history.length === 0 ? <tr><td colSpan="4" className="p-4 text-slate-400">{t("noData")}</td></tr> :
                  p.assignments_history.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100" data-testid="assignment-row">
                      <td className="p-3 text-slate-800">{a.subject_name}</td><td className="p-3 text-slate-600">{a.group_name}</td>
                      <td className="p-3"><Badge className={`border-0 ${a.status === "Actif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.status}</Badge></td>
                      <td className="p-3 text-slate-500">{(a.start_date || a.assigned_at || "").slice(0, 10)}{a.end_date ? ` → ${a.end_date}` : ""}</td>
                    </tr>))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="subjects">
          <div className="flex flex-wrap gap-2" data-testid="teacher-subjects">
            {p.subjects.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.subjects.map((s) => <Badge key={s.id} className="bg-sky-100 text-sky-700 border-0 px-3 py-1">{s.name}</Badge>)}
          </div>
        </TabsContent>

        <TabsContent value="groups">
          <div className="grid sm:grid-cols-2 gap-3" data-testid="teacher-groups">
            {p.groups.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              p.groups.map((g) => (
                <Card key={g.id} className="border-slate-200 p-3 flex items-center justify-between">
                  <div><div className="font-medium text-slate-800">{g.name}</div><div className="text-xs text-slate-400">{g.code}</div></div>
                  <Link to={`/school/groups`}><Button size="sm" variant="ghost">{t("viewGroup")}</Button></Link>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="grid md:grid-cols-3 gap-3" data-testid="teacher-schedule">
            {tt.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              DAYS.filter((d) => tt.some((s) => s.day_of_week === d)).map((d) => (
                <Card key={d} className="border-slate-200 p-3">
                  <div className="font-semibold text-slate-700 mb-2">{d}</div>
                  <div className="space-y-2">
                    {tt.filter((s) => s.day_of_week === d).map((s) => (
                      <div key={s.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid="schedule-slot">
                        <div className="font-medium text-slate-800">{s.start_time}–{s.end_time} · {s.subject_name}</div>
                        <div className="text-xs text-slate-500">{s.group_name} · {s.room_name}</div>
                      </div>))}
                  </div>
                </Card>))}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <EntityDocuments filterKey="teacher_id" id={id} testid="teacher-documents" />
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-2" data-testid="teacher-timeline">
            {timeline.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> :
              timeline.map((it, i) => (
                <div key={i} className="flex gap-3 items-start rounded-lg border border-slate-100 px-3 py-2">
                  <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                  <div><div className="text-sm font-medium text-slate-800">{it.title}</div>
                    <div className="text-xs text-slate-500">{(it.date || "").slice(0, 16).replace("T", " ")}{it.detail ? ` · ${it.detail}` : ""}</div></div>
                </div>))}
          </div>
        </TabsContent>

        {canManage && (
          <TabsContent value="notes">
            <Card className="border-slate-200 p-4 mb-3">
              <Textarea data-testid="tnote-input" placeholder={t("noteText")} value={noteText} onChange={(e) => setNoteText(e.target.value)} className="mb-2" />
              <Button data-testid="tnote-add-btn" onClick={addNote} className="gap-1"><Plus className="h-4 w-4" />{t("addNote")}</Button>
            </Card>
            <div className="space-y-2" data-testid="teacher-notes-list">
              {notes.length === 0 ? <p className="text-sm text-slate-400">{t("noNotes")}</p> :
                notes.map((n) => (
                  <Card key={n.id} className="border-slate-200 p-3 flex items-start justify-between" data-testid="tnote-item">
                    <div><div className="text-sm text-slate-800">{n.text}</div><div className="text-xs text-slate-400">{n.author_name} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                    <Button size="icon" variant="ghost" data-testid={`tnote-del-${n.id}`} onClick={() => delNote(n.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </Card>))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
